import { put, call, select, all } from 'redux-saga/effects';
import { jobsActions, jobsSelectors } from '../redux';
import {
    createJob,
    deleteJobApi,
    fetchCandidatesJobs,
    fetchJob,
    fetchJobAttributeListApi,
    fetchJobBenefitsOptionsApi,
    fetchJobRequirementsOptionsApi,
    fetchJobsPageStatistics,
    updateJob,
    updateJobStatus,
    fetchAssignedJobList,
    fetchPublicTrackableSources,
    fetchTrackableSources,
    createTrackableSource,
    fetchJobsList,
    fetchJobRequirementsRecomendations,
    fetchJobs,
    fetchJobOverviewAndResponsibilities,
    fetchActiveJobCandidates,
} from '../../apis/job';
import {
    cleanJobHtmlFields,
    formatApplyQuestionsForServer,
    formatCandidateJobFromServerFormat,
    formatFiltersToQueryParams,
    formatJobsBoardFilters,
    formatPublishedJobsFromServerFormat,
    formatJobsPageStatistics,
    formatTrackableSourceLinksToObject,
    getJobFromServerJobFormat,
    getJobRequirementsOptionsFromServerFormat,
    getJobStepsFromServerFormat,
    mergeJobStepsWithCandidates,
    updateCandidateInJobSteps,
} from './manager';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    ISaveJobAction,
    IFetchActiveJobAction,
    IUpdateActiveJobAction,
    IFetchCandidatesJobsAction,
    IUpdateJobStatusAction,
    IDeleteJobAction,
    ICreateTrackableSourceAction,
    IFetchRequirementsOptionsAction,
    IFetchJobRequirementsRecomendationsAction,
    IFetchJobOverviewAndResponsibilitiesAction,
    IFetchActiveJobCandidatesAction,
    IUpdateActiveJobCandidateAction,
    IDeleteJobTagAction,
} from '../redux/interface';
import { ISetTagAction } from '../../jobApplication/redux/interface';
import { applyTag, deleteTag } from '../../apis/jobApplication';
import { organizationSelectors } from '../../organization/redux';
import { tablesActions, tablesSelectors } from '../../tables/redux';
import { handleLanguage } from '../../user/saga/saga';
import {
    IJob,
    IJobAttribute,
    IJobBenefitsOption,
    IJobCandidatesByStep,
    IServerJobCandidate,
    IStepperInfo,
    ITag,
    JobApplicationStatusUpdateValues,
    JobStatus,
    jobStepperInfoInitState,
    SnackbarUtils,
    TableIds,
} from '../../../common';
import { formatJobsTableData } from '../../tables/saga/manager';
import { globalNavigate, ITableState } from '../../../base';
import { RoutePaths } from '../../../routes/RoutePaths';
import Cookies from 'js-cookie';
import i18next from 'i18next';

const handleJobReferral = (
    jobId: string,
    queryRef: string | null
): string | null => {
    // If there's no query ref, no need to process
    if (!queryRef) {
        return null;
    }

    const cookieKey = `job_ref_${jobId}_${queryRef}`;
    const savedRefForJob = Cookies.get(cookieKey);

    // If this exact job+ref combination was already counted, don't count it again
    if (savedRefForJob) {
        return null;
    }

    // Save this specific job+ref combination and count it
    Cookies.set(cookieKey, 'counted', { expires: 7 }); // 7 days
    return queryRef;
};

export function* fetchJobSaga(action: PayloadAction<IFetchActiveJobAction>) {
    try {
        const { jobId, isCandidate } = action.payload;
        const queryParams = new URLSearchParams(window.location.search);
        const ck = queryParams.get('ck');
        const queryRef = queryParams.get('ref');

        const referrence = handleJobReferral(jobId, queryRef);

        yield put(jobsActions.setActiveJob({ job: null }));
        yield put(
            jobsActions.setStepperInfo({
                stepperInfo: jobStepperInfoInitState,
            })
        );

        const { response } = yield call(
            fetchJob,
            jobId,
            isCandidate,
            referrence,
            ck
        );
        if (response?.status === 200) {
            if (isCandidate) {
                yield call(handleLanguage, response.data?.job_language);
            }
            yield put(
                jobsActions.setActiveJob({
                    job: getJobFromServerJobFormat(response.data),
                })
            );
        } else {
            console.log('Failed loading job'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.fetchActiveJobFailed());
    }
}

export function* fetchJobsPageStatisticsSaga() {
    try {
        const { response } = yield call(fetchJobsPageStatistics);
        if (response?.status === 200) {
            yield put(
                jobsActions.setJobsPageStatistics({
                    statistics: formatJobsPageStatistics(response.data),
                })
            );
        } else {
            console.log('Failed fetching jobs page statistics'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.fetchJobsPageStatisticsFailed());
    }
}

export function* updateJobSaga(action: PayloadAction<IUpdateActiveJobAction>) {
    try {
        const { jobId, job, isSaveAndContinue } = action.payload;
        const cleanedJob = cleanJobHtmlFields(job);
        // Format apply_questions only when this step sent them
        const formattedJob = { ...cleanedJob };
        if (cleanedJob.apply_questions !== undefined) {
            formattedJob.apply_questions = formatApplyQuestionsForServer(
                cleanedJob.apply_questions
            );
        }
        const { response } = yield call(updateJob, jobId, formattedJob);
        if (response?.status === 200) {
            const updatedJob = getJobFromServerJobFormat(response.data);
            yield put(
                jobsActions.setActiveJob({
                    job: updatedJob,
                })
            );
            const publishedJobs: Array<{
                id: number;
                title: string;
                applyQuestions: NonNullable<IJob['applyQuestions']>;
            }> = yield select(jobsSelectors.getPublishedJobs);
            if (
                publishedJobs?.some(
                    (publishedJob) => publishedJob.id === Number(jobId)
                )
            ) {
                yield put(
                    jobsActions.setPublishedJobs(
                        publishedJobs.map((publishedJob) =>
                            publishedJob.id === Number(jobId)
                                ? {
                                      ...publishedJob,
                                      applyQuestions:
                                          updatedJob.applyQuestions || [],
                                  }
                                : publishedJob
                        )
                    )
                );
            }
            if (isSaveAndContinue) {
                yield call(editJobMoveToNextStep, jobId);
            }
            yield call(
                SnackbarUtils.success,
                i18next.t('jobPage.jobEditedSuccessfully')
            );
            const newTableRow: Array<Record<string, string | number | null>> =
                formatJobsTableData([response.data]);
            yield put(
                tablesActions.updateTableRowById({
                    tableId: TableIds.JOBS,
                    newTableRow: newTableRow[0],
                })
            );
        } else {
            console.log('Failed updating job'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setUpdateJobFailed());
    }
}

export function* updateJobStatusSaga(
    action: PayloadAction<IUpdateJobStatusAction>
) {
    try {
        const { jobId, status } = action.payload;
        const { response } = yield call(updateJobStatus, jobId, status);
        if (response?.status === 200) {
            yield put(
                jobsActions.setActiveJob({
                    job: getJobFromServerJobFormat(response.data),
                })
            );
            yield call(
                SnackbarUtils.success,
                i18next.t('jobsPage.jobStatusUpdatedSuccessfully')
            );
            const newTableRow: Array<Record<string, string | number | null>> =
                formatJobsTableData([response.data]);
            yield put(
                tablesActions.updateTableRowById({
                    tableId: TableIds.JOBS,
                    newTableRow: newTableRow[0],
                })
            );
        } else {
            console.log('Failed updating job status'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setUpdateJobFailed());
    }
}

export function* deleteJobSaga(action: PayloadAction<IDeleteJobAction>) {
    try {
        const { jobId } = action.payload;
        const { response } = yield call(deleteJobApi, jobId);
        if (response?.status === 204) {
            yield put(
                jobsActions.setActiveJob({
                    job: null,
                })
            );
            yield call(SnackbarUtils.success, 'Job deleted successfully!');

            yield put(
                tablesActions.removeTableRowsById({
                    tableId: TableIds.JOBS,
                    rowIds: [jobId],
                })
            );
        } else {
            console.log('Failed to delete the job'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setUpdateJobFailed());
    }
}

export function* fetchJobAttributeListSaga() {
    try {
        const attributeList: Array<IJobAttribute> = yield select(
            jobsSelectors.getJobAttributeList
        );
        if (!attributeList.length) {
            const { response } = yield call(fetchJobAttributeListApi);
            if (response?.status === 200) {
                yield put(
                    jobsActions.setJobAttributeList({
                        jobAttributeList: response.data,
                    })
                );
            } else {
                console.log('Failed loading job attributes'); //TODO: Saga error handler
            }
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    }
}

export function* fetchJobBenefitsOptionsSaga() {
    try {
        const benefitsOptions: Array<IJobBenefitsOption> = yield select(
            jobsSelectors.getJobBenefitsOptions
        );
        if (!benefitsOptions.length) {
            const { response } = yield call(fetchJobBenefitsOptionsApi);
            if (response?.status === 200) {
                yield put(
                    jobsActions.setJobBenefitsOptions({
                        options: response.data?.results,
                    })
                );
            } else {
                console.log('Failed loading job benefits'); //TODO: Saga error handler
            }
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    }
}

export function* fetchJobRequirementsOptionsSaga(
    action: PayloadAction<IFetchRequirementsOptionsAction>
) {
    try {
        const { search } = action.payload;
        const { response } = yield call(fetchJobRequirementsOptionsApi, search);
        if (response?.status === 200) {
            yield put(
                jobsActions.setJobRequirementsOptions({
                    options: getJobRequirementsOptionsFromServerFormat(
                        response.data?.results
                    ),
                })
            );
        } else {
            console.log('Failed loading job requirements'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    }
}

export function* fetchCandidatesJobsSaga(
    action: PayloadAction<IFetchCandidatesJobsAction>
) {
    try {
        const { companyKey, filters } = action.payload;
        const queryParams: Record<string, string> = {
            ck: companyKey,
            ...formatFiltersToQueryParams(filters),
        };
        const isInitialCandidatesJobsLoading: boolean = yield select(
            jobsSelectors.getIsInitialCandidatesJobsLoading
        );
        const { response } = yield call(fetchCandidatesJobs, queryParams);
        if (response?.status === 200) {
            yield put(
                jobsActions.setCandidatesJobs({
                    jobs: formatCandidateJobFromServerFormat(
                        response.data.results
                    ),
                })
            );
            if (isInitialCandidatesJobsLoading) {
                yield put(
                    jobsActions.setJobsBoardFilters({
                        filters: formatJobsBoardFilters(response.data.results),
                    })
                );
            }
        } else {
            console.log('Failed fetching candidates jobs'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.fetchCandidatesJobsFailed());
    }
}

export function* createJobSaga(action: PayloadAction<ISaveJobAction>) {
    try {
        const { job, isSaveAndContinue } = action.payload;
        const cleanedJob = cleanJobHtmlFields(job);
        // Format apply_questions only when this step sent them
        const formattedJob = { ...cleanedJob };
        if (cleanedJob.apply_questions !== undefined) {
            formattedJob.apply_questions = formatApplyQuestionsForServer(
                cleanedJob.apply_questions
            );
        }
        const { response } = yield call(createJob, formattedJob);
        if (response?.status === 201) {
            yield put(
                jobsActions.setActiveJob({
                    job: getJobFromServerJobFormat(response.data),
                })
            );
            globalNavigate({
                pathname: `${RoutePaths.jobs}/${response.data.id}${RoutePaths.saveJob}`,
            });
            if (isSaveAndContinue) {
                yield call(editJobMoveToNextStep);
            }
            yield call(
                SnackbarUtils.success,
                i18next.t('saveJob.jobCreatedSuccessfully')
            );
            const newTableRow: Array<Record<string, string | number | null>> =
                formatJobsTableData([response.data]);
            yield put(
                tablesActions.upsertTableRow({
                    tableId: TableIds.JOBS,
                    row: newTableRow[0],
                })
            );
        } else {
            console.log('Failed creating job'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setUpdateJobFailed());
    }
}

function* editJobMoveToNextStep(jobId?: string) {
    const stepperInfo: IStepperInfo = yield select(
        jobsSelectors.getJobStepperInfo
    );
    if (jobId && stepperInfo.steps.length - 1 === stepperInfo.activeStepIndex) {
        globalNavigate({
            pathname: `${RoutePaths.jobs}/${jobId}`,
        });
    } else {
        yield put(
            jobsActions.setStepperInfo({
                stepperInfo: {
                    ...stepperInfo,
                    activeStepIndex: stepperInfo.activeStepIndex + 1,
                },
            })
        );
    }
}

export function* fetchAssignedJobListSaga() {
    try {
        const { response } = yield call(fetchAssignedJobList);
        if (response?.status === 200) {
            const assignedJobList = formatCandidateJobFromServerFormat(
                response.data
            );
            yield put(
                jobsActions.setAssignedJobList({
                    assignedJobList,
                })
            );
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setAssignedJobListDone());
    }
}

export function* fetchPublicTrackableSourcesSaga() {
    try {
        const { response } = yield call(fetchPublicTrackableSources);
        if (response?.status === 200) {
            yield put(
                jobsActions.setPublicTrackableSources({
                    trackableSources: formatTrackableSourceLinksToObject(
                        response.data.results
                    ),
                })
            );
        } else {
            console.log('Failed fetching public trackable sources');
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.fetchPublicTrackableSourcesDone());
    }
}

export function* fetchTrackableSourceSaga() {
    try {
        const { response } = yield call(fetchTrackableSources);
        if (response?.status === 200) {
            yield put(
                jobsActions.setTrackableSources({
                    trackableSources: formatTrackableSourceLinksToObject(
                        response.data.results
                    ),
                })
            );
        } else {
            console.log('Failed fetching trackable sources');
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.fetchTrackableSourcesDone());
    }
}

export function* createTrackableSourceSaga(
    action: PayloadAction<ICreateTrackableSourceAction>
) {
    try {
        const { response } = yield call(createTrackableSource, action.payload);
        if (response?.status === 201) {
            const existingSources: Record<string, string> = yield select(
                jobsSelectors.getTrackableSources
            );
            yield put(
                jobsActions.setTrackableSources({
                    trackableSources: {
                        ...existingSources,
                        [action.payload.name]: response.data.uuid,
                    },
                })
            );
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setCreateTrackableSourceDone());
    }
}

export function* fetchJobStepsSaga(
    action: PayloadAction<IFetchActiveJobAction>
) {
    try {
        const { jobId } = action.payload;
        const { response } = yield call(fetchJob, jobId);
        if (response?.status === 200) {
            const serverJob = response.data;
            yield put(
                jobsActions.setCopiedJobSteps({
                    steps: serverJob.steps
                        ? getJobStepsFromServerFormat(
                              serverJob.steps,
                              serverJob.recruiter,
                              serverJob.creator
                          )
                        : [],
                })
            );
        } else {
            yield call(SnackbarUtils.error, i18next.t('jobsPage.jobNotFound'));
        }
    } catch (error) {
        console.log(error);
    } finally {
        yield put(jobsActions.setJobStepsDone());
    }
}

export function* fetchJobsListSaga() {
    try {
        const { response } = yield call(fetchJobsList);
        if (response?.status === 200) {
            yield put(jobsActions.setJobsList({ jobs: response.data }));
        } else {
            console.log('Failed fetching jobs list'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setJobsListDone());
    }
}

export function* fetchJobRequirementsRecommendationsSaga(
    action: PayloadAction<IFetchJobRequirementsRecomendationsAction>
) {
    try {
        const { jobId } = action.payload;
        const { response } = yield call(
            fetchJobRequirementsRecomendations,
            jobId
        );
        if (response?.status === 200 || response?.status === 202) {
            console.log(
                'Job requirements recommendations request sent, waiting for socket response...'
            );
        } else {
            console.log('Failed fetching job requirements recomendations');
            yield put(jobsActions.setJobRequirementsRecomendationsDone());
        }
    } catch (error) {
        console.log(error);
        yield put(jobsActions.setJobRequirementsRecomendationsDone());
    }
}

export function* fetchPublishedJobsOptionsSaga() {
    try {
        const queryParams: Record<string, string> = {
            ordering: '-id',
            page: '1',
            status: JobStatus.Published,
        };
        const { response } = yield call(fetchJobs, queryParams);
        if (response?.status === 200) {
            const formattedPublishedJobs = formatPublishedJobsFromServerFormat(
                response.data.results
            );
            yield put(jobsActions.setPublishedJobs(formattedPublishedJobs));
        } else {
            console.log('Failed fetching published jobs options'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setPublishedJobsOptionsDone());
    }
}

export function* fetchJobOverviewAndResponsibilitiesSaga(
    action: PayloadAction<IFetchJobOverviewAndResponsibilitiesAction>
) {
    try {
        const { response } = yield call(
            fetchJobOverviewAndResponsibilities,
            action.payload
        );
        if (response?.status === 200) {
            yield put(
                jobsActions.setJobOverviewAndResponsibilities({
                    overviewAndResponsibilities: {
                        overview: response.data.job_overview,
                        responsibilities: response.data.responsibilities,
                    },
                })
            );
        } else {
            console.log('Failed fetching job overview and responsibilities'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setJobOverviewAndResponsibilitiesDone());
    }
}

export function* fetchActiveJobCandidatesSaga(
    action: PayloadAction<IFetchActiveJobCandidatesAction>
): Generator {
    try {
        const { jobId } = action.payload;
        const queryParams: Record<string, string> = {
            job_id: String(jobId),
            page_size: 'all',
        };

        // Call both APIs in parallel
        const [candidatesResult, jobResult] = yield all([
            call(fetchActiveJobCandidates, queryParams),
            call(fetchJob, String(jobId)),
        ]);

        const candidatesResponse = candidatesResult.response;
        const jobResponse = jobResult.response;

        if (candidatesResponse?.status === 200 && jobResponse?.status === 200) {
            const serverCandidates = candidatesResponse.data.results;
            const serverJob = jobResponse.data;

            // Get steps from job (in the correct order)
            const serverJobSteps = serverJob.steps || [];

            // Create job steps array from server steps
            const jobSteps = serverJobSteps.map(
                (
                    step: {
                        id: number;
                        name: string;
                    },
                    index: number
                ) => ({
                    id: String(step.id),
                    name: step.name,
                    stepOwner: null,
                    isFirstStep: index === 0,
                    isLastStep: false,
                    interviewers: [],
                })
            );

            // Add "Rejected Candidates" and "Withdrawn Candidates" steps manually at the end
            const rejectedStepId = 'rejected';
            const withdrawnStepId = 'withdrawn';
            const jobStepsWithRejectedAndWithdrawn = [
                ...jobSteps,
                {
                    id: rejectedStepId,
                    name: i18next.t(
                        'jobApplicationsPage.candidatesMenuRejectedCandidates',
                        {
                            defaultValue: 'Rejected Candidates',
                        }
                    ),
                    stepOwner: null,
                    isFirstStep: false,
                    isLastStep: false,
                    interviewers: [],
                },
                {
                    id: withdrawnStepId,
                    name: i18next.t(
                        'jobApplicationsPage.candidatesMenuWithdrawnCandidates',
                        {
                            defaultValue: 'Withdrawn Candidates',
                        }
                    ),
                    stepOwner: null,
                    isFirstStep: false,
                    isLastStep: true,
                    interviewers: [],
                },
            ];

            // Filter candidates based on current_step_status
            const rejectedCandidates = serverCandidates.filter(
                (candidate: IServerJobCandidate) =>
                    candidate.current_step_status ===
                    JobApplicationStatusUpdateValues.reject
            );
            const withdrawnCandidates = serverCandidates.filter(
                (candidate: IServerJobCandidate) =>
                    candidate.current_step_status ===
                    JobApplicationStatusUpdateValues.withdraw
            );
            const activeCandidates = serverCandidates.filter(
                (candidate: IServerJobCandidate) =>
                    candidate.current_step_status !==
                        JobApplicationStatusUpdateValues.reject &&
                    candidate.current_step_status !==
                        JobApplicationStatusUpdateValues.withdraw
            );

            // Map rejected candidates to the rejected step index (1-based)
            const rejectedStepIndex = jobStepsWithRejectedAndWithdrawn.length - 1; // second to last
            const modifiedRejectedCandidates = rejectedCandidates.map(
                (candidate: IServerJobCandidate) => ({
                    ...candidate,
                    current_step_index: rejectedStepIndex,
                })
            );

            // Map withdrawn candidates to the withdrawn step index (1-based)
            const withdrawnStepIndex = jobStepsWithRejectedAndWithdrawn.length; // last step
            const modifiedWithdrawnCandidates = withdrawnCandidates.map(
                (candidate: IServerJobCandidate) => ({
                    ...candidate,
                    current_step_index: withdrawnStepIndex,
                })
            );

            // Combine all candidates
            const allCandidates = [
                ...activeCandidates,
                ...modifiedRejectedCandidates,
                ...modifiedWithdrawnCandidates,
            ];

            // Merge steps with candidates
            const candidatesByStep = mergeJobStepsWithCandidates(
                jobStepsWithRejectedAndWithdrawn,
                allCandidates
            );

            yield put(
                jobsActions.setActiveJobCandidates({
                    candidates: candidatesByStep,
                })
            );
        } else {
            console.log('Failed fetching active job candidates or job'); //TODO: Saga error handler
        }
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.setActiveJobCandidatesDone());
    }
}

export function* updateActiveJobCandidateSaga(
    action: PayloadAction<IUpdateActiveJobCandidateAction>
): Generator {
    try {
        const { updatedCandidate, rejectedStepName, withdrawnStepName } =
            action.payload;

        // Get current active job candidates from state
        const currentCandidates: Array<IJobCandidatesByStep> = yield select(
            jobsSelectors.getActiveJobCandidates
        );

        // Update the candidate in the array using manager function
        // This will:
        // 1. Remove candidate from their old step
        // 2. Add them to the new step (based on current_step_index, REJECT, or WITHDRAW status)
        // 3. Update their tags, score, and other properties
        const updatedCandidates = updateCandidateInJobSteps(
            currentCandidates,
            updatedCandidate,
            rejectedStepName,
            withdrawnStepName
        );

        // Update the state with new candidates array
        yield put(
            jobsActions.setActiveJobCandidates({
                candidates: updatedCandidates,
            })
        );
    } catch (error) {
        console.log(error); //TODO: Saga error handler
    }
}

export function* applyTagToJobSaga(action: PayloadAction<ISetTagAction>) {
    try {
        const { response, error } = yield call(applyTag, action.payload);
        if (response?.status === 201) {
            const existingTags: Array<ITag> = yield select(
                organizationSelectors.getTags
            );
            const appliedTagValues = existingTags.find(
                (tag) => tag.tag_id === action.payload.tag
            );
            const newTag: ITag = {
                id: response.data.id,
                tag_id: appliedTagValues?.tag_id || action.payload.tag,
                label: appliedTagValues?.label || '',
                color: appliedTagValues?.color || '',
            };

            if (action.payload.isInTableRow) {
                const tableData: Array<Record<string, any>> = yield select(
                    tablesSelectors.getRowsData,
                    TableIds.JOBS
                );
                const tableState: ITableState = yield select(
                    tablesSelectors.getTablesState,
                    TableIds.JOBS
                );
                const updatedTableData = tableData.map((row) => {
                    if (Number(row.id) === Number(action.payload.object_id)) {
                        return {
                            ...row,
                            appliedTags: [...(row.appliedTags || []), newTag],
                        };
                    }
                    return row;
                });
                yield put(
                    tablesActions.setRowsDataById({
                        tableId: TableIds.JOBS,
                        data: updatedTableData,
                        page: tableState.currentPage,
                    })
                );
            } else {
                const activeJob: IJob = yield select(jobsSelectors.getActiveJob);
                if (activeJob) {
                    yield put(
                        jobsActions.setActiveJob({
                            job: {
                                ...activeJob,
                                tags: [...(activeJob.tags || []), newTag],
                            },
                        })
                    );
                }
            }
        } else {
            yield call(SnackbarUtils.error, error?.data?.[0]);
        }
    } catch (error) {
        console.log(`Failed to set job tag: ${error}`); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.applyTagToJobDone());
    }
}

export function* deleteTagFromJobSaga(
    action: PayloadAction<IDeleteJobTagAction>
) {
    try {
        const { id, isInTableRow, jobId } = action.payload;
        const { response, error } = yield call(deleteTag, id);
        if (response?.status === 204 || response?.status === 200) {
            if (isInTableRow) {
                const tableData: Array<Record<string, any>> = yield select(
                    tablesSelectors.getRowsData,
                    TableIds.JOBS
                );
                const tableState: ITableState = yield select(
                    tablesSelectors.getTablesState,
                    TableIds.JOBS
                );
                const updatedTableData = tableData.map((row) => {
                    if (Number(row.id) === Number(jobId)) {
                        return {
                            ...row,
                            appliedTags: (row.appliedTags || []).filter(
                                (tag: ITag) => Number(tag.id) !== Number(id)
                            ),
                        };
                    }
                    return row;
                });
                yield put(
                    tablesActions.setRowsDataById({
                        tableId: TableIds.JOBS,
                        data: updatedTableData,
                        page: tableState.currentPage,
                    })
                );
            } else {
                const activeJob: IJob = yield select(jobsSelectors.getActiveJob);
                if (activeJob) {
                    yield put(
                        jobsActions.setActiveJob({
                            job: {
                                ...activeJob,
                                tags: (activeJob.tags || []).filter(
                                    (tag) => Number(tag.id) !== Number(id)
                                ),
                            },
                        })
                    );
                }
            }
        } else {
            yield call(SnackbarUtils.error, error?.data?.[0]);
        }
    } catch (error) {
        console.log(`Failed to delete job tag: ${error}`); //TODO: Saga error handler
    } finally {
        yield put(jobsActions.deleteTagFromJobDone());
    }
}
